import { Skeleton } from '@/components/ui/skeleton';

/**
 * What a route shows while it is still arriving.
 *
 * THE ROUTER HAD NO PENDING STATE AT ALL, and the gap is not cosmetic. Every
 * page in this app is a `lazyRouteComponent`, so navigation does not commit
 * until the module resolves. Until it commits, `useLocation()` still reports
 * the OLD path — and the sidebar's active indicator reads exactly that. So a
 * slow route leaves the highlight on the previous item, the content unchanged,
 * and nothing anywhere saying a thing is happening.
 *
 * A click that does nothing for several seconds is read as a broken click, not
 * a slow one. That is what it looked like on a cold dev server, where the first
 * transform of a route can take the better part of a minute: the sidebar
 * appeared frozen and the app appeared dead. It is the same in production the
 * first time a chunk is fetched on a poor connection — rarer, quieter, and
 * indistinguishable from a bug when it happens.
 *
 * Deliberately a skeleton of the page frame rather than a spinner. A spinner
 * centred in the viewport replaces the layout and makes the app flash between
 * two unrelated pictures; a frame that matches what is about to arrive reads as
 * the page loading rather than the app reloading.
 *
 * `defaultPendingMs` decides when this appears. It stays at the router's
 * default so a fast navigation never flashes it — this is for the slow case,
 * and showing it for 80ms would be its own kind of noise.
 */
export function RoutePending() {
  return (
    <div
      className="w-full space-y-6 p-6"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading page…</span>
      {/* Title + subtitle, the shape nearly every page in this app opens with. */}
      <div className="space-y-2">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80" />
      </div>
      {/* A KPI strip, which the dashboard, client detail and portfolio pages
          all lead with. Four is the common width. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}
